import {
  AppstoreOutlined,
  BarsOutlined,
  DownOutlined,
  FilterOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Badge, Dropdown, Menu } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import NewDrawerFilter from 'components/UI/NewDrawerFilter';
import { useBreakpoints } from 'hooks/useMediaQuery';
import { ModeType } from 'screens/Products/utils/enums/ModeType.enum';
import {
  getModeListProductList,
  setModeListProductList,
  setOfferTypeProductList,
  setPageProductList,
} from 'services/config';
import {
  useGetDirectedOffersCountQuery,
  useGetMyOffersCountQuery,
} from 'services/ecommerceApi';
import { getShowPinnedFilter } from 'store/state/offerFilters/offerFiltersSelectors';
import { offerFiltersSelectors, sessionSelectors } from 'store/state/selectors';
import { offerFiltersActions } from 'store/state/slices';
import { RootState } from 'store/store';
import { redirectMyOffers } from 'utils/util';

import * as S from './styles';

interface NewCategoriesFilterProps {
  showFilterAndViewControls?: boolean;
}

const getModeIcon = (mode: ModeType) => {
  switch (mode) {
    case ModeType.Grid:
      return <AppstoreOutlined />;
    case ModeType.List:
      return <UnorderedListOutlined />;
    case ModeType.MinifyList:
      return <BarsOutlined />;
    default:
      return <UnorderedListOutlined />;
  }
};

const getModeTitle = (mode: ModeType) => {
  switch (mode) {
    case ModeType.Grid:
      return 'Grade';
    case ModeType.List:
      return 'Lista';
    case ModeType.MinifyList:
      return 'Lista Compacta';

    default:
      return 'Lista';
  }
};

export default function NewCategoriesFilter({
  showFilterAndViewControls = false,
}: NewCategoriesFilterProps) {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);
  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );
  const { isNotDesktop } = useBreakpoints();
  const { deliveryDate, producerUrl } = useSelector(sessionSelectors.getRoot);
  const { categories: [category] = [], offerTypes: offerType = [] } =
    useSelector((state: RootState) => state.offerFilters);
  const offersFilters = useSelector(offerFiltersSelectors.getOfferFilters);
  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );
  const lengthItems = offerType?.length ?? 0;
  const isLkpOnly =
    lengthItems > 0 &&
    offerType.every(
      (type) => type === 'inTransit' || type === 'received',
    );
  const showPinnedFilter = useSelector(getShowPinnedFilter);

  const [currentModeList, setCurrentModeList] = useState<ModeType>(() => {
    const storedMode = getModeListProductList();
    return storedMode ?? ModeType.List;
  });

  const [visibleFilter, setVisibleFilter] = useState(false);

  const { data: countDirectedOffers } = useGetDirectedOffersCountQuery(
    { deliveryDate, customerId: selectedCustomerId },
    { skip: !selectedCustomerId },
  );

  const { data: countMyOffers } = useGetMyOffersCountQuery(
    { deliveryDate, customerId: selectedCustomerId },
    { skip: !selectedCustomerId },
  );

  const filterCategory = useCallback(
    (category: string | number) => {
      setPageProductList(1);
      setOfferTypeProductList([]);
      dispatch(
        offerFiltersActions.filter({
          ...offersFilters,
          categories: [+category],
        }),
      );
      dispatch(
        offerFiltersActions.setFilterByHeader({ categories: [+category] }),
      );
      navigate(`/${producerUrl}/products`);
    },
    [dispatch, navigate, offersFilters, producerUrl],
  );

  const filterProducts = useCallback(
    (key: string | string[]) => {
      setPageProductList(1);
      const keyValue = Array.isArray(key) ? key : [key];
      dispatch(
        offerFiltersActions.filter({
          ...offersFilters,
          offerTypes: keyValue,
        }),
      );
      setOfferTypeProductList(keyValue);
      navigate(`/${producerUrl}/products`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch, navigate, offersFilters, producerUrl],
  );

  const isActive = useCallback(
    (condition: boolean) => location.pathname.includes('products') && condition,
    [location.pathname],
  );

  useEffect(() => {
    if (location.pathname.includes('products') && !offerType) {
      dispatch(
        offerFiltersActions.filter({
          ...offersFilters,
          offerTypes: [],
        }),
      );
    }
  }, [location.pathname, offerType, dispatch, offersFilters]);

  useEffect(() => {
    if ((slug || slug === '') && +slug !== category) {
      filterCategory(slug);
    }
  }, [slug, category, filterCategory]);

  const handleFilterClick = useCallback(() => {
    if (!showPinnedFilter) {
      setVisibleFilter(true);
    } else {
      dispatch(offerFiltersActions.setShowPinnedFilter(false));
    }
  }, [dispatch, showPinnedFilter]);

  const handleClickCloseFilter = useCallback(() => {
    setVisibleFilter(false);
  }, []);

  const handleModeSelect = useCallback(
    (mode: ModeType) => {
      setModeListProductList(mode);
      setCurrentModeList(mode);
      dispatch(offerFiltersActions.setTypeMenu(mode));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch],
  );

  const viewModeMenu = (
    <Menu>
      {[ModeType.Grid, ModeType.List, ModeType.MinifyList].map((mode) => (
        <S.ViewModeMenuItem
          key={mode}
          onClick={() => handleModeSelect(mode)}
          $isSelected={currentModeList === mode}
        >
          <S.ViewModeItemContent>
            <S.ViewModeIcon $isSelected={currentModeList === mode}>
              {getModeIcon(mode)}
            </S.ViewModeIcon>
            <S.ViewModeText $isSelected={currentModeList === mode}>
              {getModeTitle(mode)}
            </S.ViewModeText>
            {currentModeList === mode && <S.ViewModeCheck />}
          </S.ViewModeItemContent>
        </S.ViewModeMenuItem>
      ))}
    </Menu>
  );

  const selectedKeys = useMemo(() => {
    const keys: string[] = [];

    if (location.pathname.includes('products')) {
      // "Todas as Ofertas"
      if (!offerType || offerType.length === 0 || offerType.length >= 3) {
        keys.push('Todos');
      }

      // "Estoque no Produtor"
      if (offerType.includes('onSite') && lengthItems === 1) {
        keys.push('onSite');
      }

      // "LKP"
      if (isLkpOnly) {
        keys.push('lkp');
      }

      // "Ofertas exclusivas"
      if (location.pathname.includes('/my-offers')) {
        keys.push('Ofertas exclusivas');
      }

      // "Ofertas direcionadas"
      if (offerType.includes('directed') && lengthItems === 1) {
        keys.push('Ofertas direcionadas');
      }
    }

    return keys;
  }, [location.pathname, offerType, lengthItems, isLkpOnly]);

  return (
    <>
      <S.GlobalMenuStyles />
      <S.Root className="new-categories-filter">
        <S.FilterContainer hasControls={showFilterAndViewControls}>
          {showFilterAndViewControls && isNotDesktop && (
            <S.LeftSection>
              <S.FilterIcon onClick={handleFilterClick}>
                <FilterOutlined />
              </S.FilterIcon>
            </S.LeftSection>
          )}

          <S.CenterSection>
            <S.ResponsiveMenu
              mode="horizontal"
              triggerSubMenuAction={isMobile ? 'click' : 'hover'}
              overflowedIndicator={<DownOutlined />}
              selectedKeys={selectedKeys}
            >
              {!isAuthenticated && (
                <S.CategoryMenuItem
                  onClick={() => filterProducts([])}
                  key="Todos"
                  active={
                    !!isActive(
                      !offerType ||
                        offerType.length === 0 ||
                        offerType.length >= 3,
                    )
                  }
                  className="active-filter"
                >
                  Todas as Ofertas
                </S.CategoryMenuItem>
              )}

              {isAuthenticated && (
                <>
                  <S.CategoryMenuItem
                    onClick={() => filterProducts([])}
                    key="Todos"
                    active={
                      !!isActive(
                        !offerType ||
                          offerType.length === 0 ||
                          offerType.length >= 3,
                      )
                    }
                    className={
                      isActive(
                        !offerType ||
                          offerType.length === 0 ||
                          offerType.length >= 3,
                      )
                        ? 'active-filter'
                        : ''
                    }
                  >
                    Todas as Ofertas
                  </S.CategoryMenuItem>

                  <S.CategoryMenuItem
                    onClick={() => filterProducts('onSite')}
                    key="onSite"
                    active={
                      !!isActive(
                        offerType.includes('onSite') && lengthItems === 1,
                      )
                    }
                    className={
                      offerType.includes('onSite') && lengthItems === 1
                        ? 'active-filter'
                        : ''
                    }
                  >
                    Estoque no Produtor
                  </S.CategoryMenuItem>

                  <S.CategoryMenuItem
                    onClick={() => filterProducts(['inTransit', 'received'])}
                    key="lkp"
                    active={!!isActive(isLkpOnly)}
                    className={isActive(isLkpOnly) ? 'active-filter' : ''}
                  >
                    LKP
                  </S.CategoryMenuItem>
                </>
              )}

              {!isProducerAuthenticated && isAuthenticated && (
                <>
                  <S.CategoryMenuItem
                    onClick={() => filterProducts(['directed'])}
                    key="Ofertas direcionadas"
                    active={
                      !!isActive(
                        offerType.includes('directed') && lengthItems === 1,
                      )
                    }
                    className={
                      isActive(
                        offerType.includes('directed') && lengthItems === 1,
                      )
                        ? 'active-filter'
                        : ''
                    }
                  >
                    <Badge
                      size="small"
                      count={countDirectedOffers}
                      color="#FFC000"
                    >
                      Ofertas Direcionadas
                    </Badge>
                  </S.CategoryMenuItem>
                  <S.CategoryMenuItem
                    onClick={() => redirectMyOffers(producerUrl)}
                    key="Ofertas exclusivas"
                    active={
                      !!isActive(location.pathname.includes('/my-offers'))
                    }
                    className={
                      isActive(location.pathname.includes('/my-offers'))
                        ? 'active-filter'
                        : ''
                    }
                  >
                    <Badge size="small" count={countMyOffers} color="#FFC000">
                      Ofertas Exclusivas
                    </Badge>
                  </S.CategoryMenuItem>
                </>
              )}
            </S.ResponsiveMenu>
          </S.CenterSection>

          {showFilterAndViewControls && isAuthenticated && (
            <S.RightSection>
              <S.TabletOnlyText variant="primary">Exibição em</S.TabletOnlyText>
              <Dropdown
                overlay={viewModeMenu}
                trigger={['click']}
                placement="bottomRight"
              >
                <S.GridIcon isActive={false}>
                  <S.DropdownContent>
                    {getModeIcon(currentModeList)}
                    <S.TabletOnlyText variant="secondary">
                      {getModeTitle(currentModeList)}
                    </S.TabletOnlyText>
                    <DownOutlined style={{ fontSize: '0.75rem' }} />
                  </S.DropdownContent>
                </S.GridIcon>
              </Dropdown>
            </S.RightSection>
          )}
        </S.FilterContainer>{' '}
      </S.Root>

      {showFilterAndViewControls && (
        <NewDrawerFilter
          onClose={handleClickCloseFilter}
          visible={visibleFilter}
        />
      )}
    </>
  );
}
